/**
 * RelativeTime primitive — Plan 02-10 / UI-SPEC §"<RelativeTime>".
 *
 * Wraps the Norwegian relative-time formatter. Auto-refreshes once per
 * minute via a single shared useSyncExternalStore subscription so a
 * page with N RelativeTime instances ticks all of them at the same
 * minute boundary (one timer total, not N).
 *
 * Output (Bokmål):
 *   <60s        → "akkurat nå"
 *   1–59 min    → "for {n} minutter siden" (singular: "minutt")
 *   1–23 timer  → "for {n} timer siden" (singular: "time")
 *   1–6 dager   → "for {n} dager siden" (singular: "dag")
 *   ≥7 dager    → falls back to formatDateLong (absolute)
 *
 * Plan 02-10 ships this primitive — no Phase 1 helper exists for relative
 * time, so the formatter is co-located with the component (single
 * Norwegian locale today; future English is a content-only swap on the
 * unit nouns).
 */
import {
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { formatDateLong } from '../../i18n/formatters';

const MIN_MS = 60_000;
const HOUR_MS = 60 * MIN_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

/**
 * Norwegian Bokmål relative-time formatter.
 *
 * Pure function — caller passes in `now` for testability. Falls back to
 * the long-form absolute date for instants ≥7 days old (UI-SPEC §
 * "<RelativeTime>" — "Falls back to absolute date format after 7 days").
 */
export function formatDateRelative(
  instantIso: string,
  now: Date = new Date(),
  locale: string = 'nb-NO',
): string {
  void locale; // single locale today; reserved for future English.
  const at = new Date(instantIso);
  const diff = now.getTime() - at.getTime();
  if (diff < MIN_MS) return 'akkurat nå';
  if (diff < HOUR_MS) {
    const n = Math.floor(diff / MIN_MS);
    return n === 1 ? 'for 1 minutt siden' : `for ${n} minutter siden`;
  }
  if (diff < DAY_MS) {
    const n = Math.floor(diff / HOUR_MS);
    return n === 1 ? 'for 1 time siden' : `for ${n} timer siden`;
  }
  if (diff < WEEK_MS) {
    const n = Math.floor(diff / DAY_MS);
    return n === 1 ? 'for 1 dag siden' : `for ${n} dager siden`;
  }
  return formatDateLong(at);
}

// ─── Shared minute-tick store (one timer for the whole app) ────────────────

let _tickInterval: ReturnType<typeof setInterval> | null = null;
let _tickListeners = 0;
const _subs = new Set<() => void>();
let _tickSnapshot = 0;

function startTickIfNeeded(): void {
  if (_tickInterval !== null) return;
  _tickInterval = setInterval(() => {
    _tickSnapshot += 1;
    for (const fn of _subs) fn();
  }, MIN_MS);
}

function stopTickIfUnused(): void {
  if (_tickListeners > 0) return;
  if (_tickInterval !== null) {
    clearInterval(_tickInterval);
    _tickInterval = null;
  }
}

function subscribeTick(cb: () => void): () => void {
  _subs.add(cb);
  _tickListeners += 1;
  startTickIfNeeded();
  return () => {
    _subs.delete(cb);
    _tickListeners = Math.max(0, _tickListeners - 1);
    stopTickIfUnused();
  };
}

function getTickSnapshot(): number {
  return _tickSnapshot;
}

interface RelativeTimeProps {
  /** ISO instant string. */
  instant: string;
  /** Locale override; default 'nb-NO' (single supported today). */
  locale?: string;
  className?: string;
}

export function RelativeTime({
  instant,
  locale = 'nb-NO',
  className,
}: RelativeTimeProps): ReactNode {
  // Re-render once per minute via the shared tick store.
  useSyncExternalStore(subscribeTick, getTickSnapshot, getTickSnapshot);
  const text = formatDateRelative(instant, new Date(), locale);
  return (
    <time
      data-testid="relative-time"
      dateTime={instant}
      className={className}
    >
      {text}
    </time>
  );
}
